import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Sparkles } from "lucide-react";
import type { AddItemPhase } from "@/lib/generationRequests";

export type AddItemLevel = "knowledge" | "understanding" | "higherOrderThinking";

/**
 * Live state of an in-flight "Add item" generation (ARC-30 cap 3). Driven by
 * the parent, which owns the generation, so it survives this control
 * unmounting and remounting mid-run.
 */
export type AddItemRun = {
  /** Epoch ms the generation started, for the elapsed clock. */
  startedAt: number;
  phase: AddItemPhase;
  /** The status poll is failing but still retrying. */
  degraded: boolean;
};

const PHASE_LABEL: Record<AddItemPhase, string> = {
  submitting: "Sending your request",
  generating: "Writing the item",
  retrieving: "Fetching the finished item",
};

function elapsedLabel(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

interface AddItemControlProps {
  topics: { id: string; name: string }[];
  total: number;
  cap: number;
  /** True while an AI item is being generated. */
  busy: boolean;
  /** The topic an in-flight generation is for. Driven by the parent so the
   *  trigger keeps showing it even if this control unmounts/remounts (e.g. the
   *  options card is collapsed then reopened) mid-generation. */
  generatingTopicId?: string | null;
  /** Live phase + elapsed time for the in-flight generation, if any. */
  run?: AddItemRun | null;
  /** Hide the "Add item" heading (e.g. when shown inside a dialog that already
   *  has a title). */
  hideHeading?: boolean;
  onAdd: (args: { topicId: string; level: AddItemLevel; mode: "ai" | "blank" }) => void;
}

const LEVELS: { value: AddItemLevel; label: string }[] = [
  { value: "knowledge", label: "Knowledge" },
  { value: "understanding", label: "Understanding" },
  { value: "higherOrderThinking", label: "Application+" },
];

// Options-area control for adding one item to a chosen topic — either
// AI-generated (Blacksmith, at the chosen cognitive level) or a blank item to
// fill in. Enforced against the test's item cap.
export function AddItemControl({ topics, total, cap, busy, generatingTopicId, run, hideHeading, onAdd }: AddItemControlProps) {
  // No topic preselected — the trigger reads "Select Topic" until the user
  // picks one (and resets back to it after each add). This also sidesteps the
  // stray leading space that showed when the first topic was auto-selected.
  const [topicId, setTopicId] = useState<string>("");
  const [level, setLevel] = useState<AddItemLevel>("knowledge");

  // Buttons stay enabled at the cap so the click reaches the parent, which
  // shows the "limit reached" popup — rather than silently disabling here.
  const disabled = busy || !topicId;

  // While a generation is in flight, keep the chosen topic shown in the trigger
  // (and lock the select) so the user can see which item is cooking. Once it
  // returns (busy true -> false), clear back to "Select Topic".
  const prevBusy = useRef(busy);
  useEffect(() => {
    if (prevBusy.current && !busy) setTopicId("");
    prevBusy.current = busy;
  }, [busy]);

  // Elapsed clock for the in-flight generation. Ticks on its own rather than
  // waiting for the next status poll, so the number visibly moves every second
  // — the whole point of ARC-30 cap 3 is that "Add item" stops being a spinner
  // you cannot tell apart from a hang. Computed from the parent's startedAt so
  // collapsing and reopening this card mid-run doesn't restart the clock.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!run) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [run?.startedAt]);
  const elapsed = run ? elapsedLabel(now - run.startedAt) : null;

  const handleAdd = (mode: "ai" | "blank") => {
    if (!topicId) return;
    onAdd({ topicId, level, mode });
    // Blank items add instantly, so clear now; AI items keep showing the topic
    // until generation finishes (cleared by the busy effect above).
    if (mode === "blank") setTopicId("");
  };

  // Topic titles start with a letter/number, but the AI sometimes prepends
  // junk — ordinary spaces, zero-width chars, BOM, or even "blank" glyphs like
  // the Braille blank (U+2800) that earlier whitespace-class regexes missed.
  // Strip EVERYTHING before the first letter/number so no stray space can show.
  const stripName = (n: string) => n.replace(/^[^\p{L}\p{N}]+/u, "");
  // While generating, show the parent-driven topic (survives unmount/remount);
  // otherwise the locally-picked one.
  const shownTopicId = busy && generatingTopicId ? generatingTopicId : topicId;
  const selectedTopic = topics.find((t) => t.id === shownTopicId);

  // Show the orange ring while the menu is open (not just after a pick), and
  // draw it INSET so it isn't clipped on the left/right. The add-item block
  // lives in an overflow-y-auto scroll area, which forces overflow-x to clip
  // too, cutting the default outward ring at the sides — an inset ring stays
  // inside the trigger, so nothing moves and it's visible on all four sides.
  const triggerRing =
    "data-[state=open]:ring-2 data-[state=open]:ring-ring data-[state=open]:ring-inset " +
    "focus:ring-inset focus:ring-offset-0";

  return (
    <div className="space-y-2">
      {!hideHeading && <h3 className="font-semibold">Add item</h3>}

      <>
          {/* Locked while generating so the trigger keeps showing the topic
              being generated until the item returns. */}
          <Select value={shownTopicId} onValueChange={setTopicId} disabled={busy}>
            <SelectTrigger className={`w-full ${triggerRing}`} data-testid="add-item-topic">
              {/* Render the trigger text ourselves instead of <SelectValue>:
                  Radix's SelectValue mirrors the selected item's own text (not
                  our children), which is where the stray leading space slipped
                  through. This span shows the stripped name, full stop. */}
              {selectedTopic ? (
                <span className="line-clamp-1 text-left">{stripName(selectedTopic.name)}</span>
              ) : (
                <span className="line-clamp-1 text-left text-muted-foreground">Select Topic</span>
              )}
            </SelectTrigger>
            <SelectContent>
              {topics.map((t) => {
                const name = stripName(t.name);
                return (
                  // pl-2 removes shadcn's default pl-8 check-gutter (the
                  // "space" in front of names); hide the now-redundant check
                  // indicator since the trigger already shows the selection.
                  <SelectItem
                    key={t.id}
                    value={t.id}
                    textValue={name}
                    className="pl-2 [&>span:first-child]:hidden"
                  >
                    {name}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          <Select value={level} onValueChange={(v) => setLevel(v as AddItemLevel)}>
            <SelectTrigger className={`w-full ${triggerRing}`} data-testid="add-item-level">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LEVELS.map((l) => (
                <SelectItem key={l.value} value={l.value}>
                  {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex flex-col gap-2">
            <Button
              size="sm"
              className="w-full"
              disabled={disabled}
              onClick={() => handleAdd("ai")}
              data-testid="add-item-ai"
            >
              {busy ? (elapsed ? `Generating ${elapsed}` : "Generating") : "Generate"}
              {busy ? (
                <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 shrink-0" />
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              disabled={disabled}
              onClick={() => handleAdd("blank")}
              data-testid="add-item-blank"
            >
              Blank <Plus className="w-4 h-4 shrink-0" />
            </Button>
          </div>

          {/* ARC-30 cap 3: what is actually happening, instead of an 8-minute
              silent POST behind a spinner. The phase comes from the real BCE
              session poll, not from a timer pretending to know. */}
          {busy && run && (
            <p className="text-xs text-muted-foreground" data-testid="add-item-progress">
              {run.degraded
                ? "Having trouble reaching the server — still retrying."
                : `${PHASE_LABEL[run.phase]}… a single item usually takes under a minute.`}
            </p>
          )}
        </>
    </div>
  );
}
